import store from '../state/store'
import { getAuthBackend } from './authApi'

/**
 * AuthService - Handles activity monitoring and proactive token management
 * Delegates actual auth operations to AuthBackend in authApi.js
 */
class AuthService {
    constructor() {
        this.tokenCheckInterval = null
        this.lastActivity = Date.now()
        this.isUserActive = true
        this.isRefreshing = false
    }

    /**
     * Start monitoring token expiration with smart intervals
     */
    startTokenMonitoring() {
        // Clear any existing interval
        this.stopTokenMonitoring()
        
        // Start with activity monitoring
        this.setupActivityMonitoring()
        
        // Check token every 5 minutes (only if user is active)
        this.tokenCheckInterval = setInterval(() => {
            // Only check if user has been active in the last 10 minutes
            if (this.isUserActive && (Date.now() - this.lastActivity) < 10 * 60 * 1000) {
                this.checkTokenStatus()
            }
        }, 5 * 60 * 1000) // 5 minutes
    }

    /**
     * Setup activity monitoring to track user engagement
     */
    setupActivityMonitoring() {
        const updateActivity = () => {
            this.lastActivity = Date.now()
            this.isUserActive = true
        }

        // Track user activity
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
        
        events.forEach(event => {
            document.addEventListener(event, updateActivity, { passive: true })
        })

        // Check token immediately on user activity (throttled)
        let activityTimeout = null
        const throttledCheck = () => {
            if (activityTimeout) return
            
            activityTimeout = setTimeout(() => {
                this.checkTokenStatus()
                activityTimeout = null
            }, 60000) // Maximum once per minute on activity
        }

        events.forEach(event => {
            document.addEventListener(event, throttledCheck, { passive: true })
        })
    }

    /**
     * Stop monitoring token expiration
     */
    stopTokenMonitoring() {
        if (this.tokenCheckInterval) {
            clearInterval(this.tokenCheckInterval)
            this.tokenCheckInterval = null
        }
        this.isUserActive = false
    }

    /**
     * Check token status and refresh if needed
     * Delegates to AuthBackend for actual logic
     */
    async checkTokenStatus() {
        if (this.isRefreshing) {
            return false
        }

        const authBackend = getAuthBackend()
        if (!authBackend) {
            console.warn('Auth backend not initialized')
            return false
        }

        const authUser = store.state.auth.authUser
        if (!authUser?.access_token) {
            return false
        }

        // Use AuthBackend's token expiration check
        if (authBackend.isTokenExpired()) {
            console.log('Token is expired or will expire soon, refreshing...')

            this.isRefreshing = true
            try {
                await authBackend.refreshToken()
                return true
            } catch (error) {
                console.error('Failed to refresh token:', error)
                // Let authBackend handle the failure (it has the redirect logic)
                return false
            } finally {
                this.isRefreshing = false
            }
        }

        return true
    }

    /**
     * Initialize authentication service
     */
    init() {
        // Check if user is already logged in
        const authUser = store.state.auth.authUser
        if (authUser?.access_token) {
            // Validate the current token immediately
            this.checkTokenStatus()
        }

        // Listen for auth state changes
        store.watch(
            (state) => state.auth.authUser,
            (newAuthUser, oldAuthUser) => {
                if (newAuthUser?.access_token && !oldAuthUser?.access_token) {
                    // User just logged in, start monitoring
                    this.startTokenMonitoring()
                } else if (!newAuthUser?.access_token && oldAuthUser?.access_token) {
                    // User just logged out, stop monitoring
                    this.stopTokenMonitoring()
                }
            }
        )

        // Listen for storage events (for multi-tab synchronization)
        window.addEventListener('storage', (event) => {
            if (event.key === 'authUser') {
                // Another tab updated the auth state
                store.dispatch('auth/syncFromLocalStorage')
            }
        })

        // Listen for page visibility changes
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden && store.state.auth.authUser?.access_token) {
                // Page became visible, check token status
                this.checkTokenStatus()
            }
        })

        // Check token when page loads
        document.addEventListener('DOMContentLoaded', () => {
            if (store.state.auth.authUser?.access_token) {
                this.checkTokenStatus()
            }
        })
    }

    /**
     * Proxy methods to AuthBackend for convenience
     */
    getAccessToken() {
        const authBackend = getAuthBackend()
        return authBackend ? authBackend.getAuthToken() : null
    }

    isAuthenticated() {
        const authBackend = getAuthBackend()
        const authUser = store.state.auth.authUser
        return !!(authUser?.access_token && authBackend && !authBackend.isTokenExpired())
    }

    async logout() {
        try {
            await store.dispatch('auth/logOut')
        } catch (error) {
            console.error('Logout error:', error)
        } finally {
            this.stopTokenMonitoring()
        }
    }
}

// Create singleton instance
const authService = new AuthService()

export default authService 